/**
 * MARA discovery: GET {mara}/api/info (mara/app/api/misc/routes.py:25-43) names the Workspace API and the accounts
 * API. Never hard-code the Workspace API host (docs/PLAN.md "Discovery"). Cached per session, per MARA origin.
 * Live answer on 2026-10-02 (docs/look-spec/mara-api-info-2026-10-02.json): workspace_api_url
 * https://workspaces.nanome.ai, accounts_api_url https://api.nanome.com, oauth_enabled true.
 */
import { ApiError, isAbortError } from './errors';

export interface MaraInfo {
  workspace_api_url: string;
  accounts_api_url: string;
  oauth_enabled: boolean;
  enable_credits: boolean;
  require_verified_email: boolean;
  /** Other fields MARA returns (ai_enabled, version, voice_enabled, ...), kept as they came. */
  [key: string]: unknown;
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface DiscoveryOptions {
  fetch?: FetchLike;
  signal?: AbortSignal;
  /** Bypass the cache. */
  force?: boolean;
}

export const DEFAULT_MARA_URL = 'https://app.nanome.ai';

const cache = new Map<string, Promise<MaraInfo>>();

export const trimSlash = (url: string) => url.replace(/\/+$/, '');

export function clearMaraInfoCache(): void { cache.clear(); }

/** GET {mara}/api/info, cached per MARA origin for the session. A failed fetch is not cached. */
export function getMaraInfo(maraUrl: string = DEFAULT_MARA_URL, options: DiscoveryOptions = {}): Promise<MaraInfo> {
  const mara = trimSlash(maraUrl);
  const cached = cache.get(mara);
  if (cached && !options.force) return cached;
  const p = fetchInfo(mara, options).catch(e => { if (cache.get(mara) === p) cache.delete(mara); throw e; });
  cache.set(mara, p);
  return p;
}

async function fetchInfo(mara: string, options: DiscoveryOptions): Promise<MaraInfo> {
  const url = `${mara}/api/info`;
  const f: FetchLike = options.fetch ?? ((i, init) => globalThis.fetch(i, init));
  let res: Response;
  try {
    res = await f(url, { method: 'GET', signal: options.signal ?? null });
  } catch (e) {
    throw new ApiError('info', isAbortError(e) ? 'aborted' : `GET ${url} failed: ${(e as Error)?.message ?? e}`, { status: 0, url, method: 'GET', code: isAbortError(e) ? 'ABORTED' : 'NETWORK', cause: e });
  }
  const text = await res.text();
  if (!res.ok) throw new ApiError('info', `GET ${url} returned HTTP ${res.status}`, { status: res.status, body: text, url, method: 'GET' });
  let info: MaraInfo;
  try { info = JSON.parse(text); } catch (e) { throw new ApiError('info', `GET ${url} did not return JSON`, { status: res.status, body: text, url, method: 'GET', cause: e }); }
  if (typeof info?.workspace_api_url !== 'string' || !info.workspace_api_url) {
    throw new ApiError('info', `${url} names no workspace_api_url`, { status: res.status, body: text, url, method: 'GET' });
  }
  return {
    ...info,
    workspace_api_url: trimSlash(info.workspace_api_url),
    accounts_api_url: trimSlash(typeof info.accounts_api_url === 'string' ? info.accounts_api_url : ''),
    oauth_enabled: info.oauth_enabled === true,
    enable_credits: info.enable_credits === true,
    require_verified_email: info.require_verified_email === true,
  };
}
